import { PagingParameters } from "@/api/dtos";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { FieldConfig } from "@/config/forms/general";
import { Exhibition, Tirage, TirageInExhibition } from "@/lib/types";
import { ColumnDef, Row } from "@tanstack/react-table";
import { ChevronLeft, PlusIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import TirageInExhibitionDrawer from "./drawer/TirageInExhibitionDrawer";

interface ComplexLinkProps {
  id: string;
  tirage: Tirage | undefined;
  exhibition: Exhibition | undefined;
  fields: FieldConfig[] | undefined;
  type: LinkableType;
}

/**
 * TirageInExhibitions component
 * @returns {JSX.Element}
 */
const TirageInExhibitions = ({
  id,
  tirage,
  exhibition,
  fields,
  type,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: complexeRelation } = useGetComplexRelationFields(
    id,
    "tirageInExhibition",
    type,
  );

  const { mutate: deleteTirageInExhibition } = useDeleteComplexLink(
    id,
    "tirageInExhibition",
    type,
  );
  const [searchContent, setSearchContent] = useState<string>("");

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteTirageInExhibition({ id, relation: "tirageInExhibition" });
    },
    [deleteTirageInExhibition],
  );

  const columns: ColumnDef<TirageInExhibition, any>[] = useMemo(() => {
    return [
      ...(type === "tirage"
        ? [
            {
              accessorKey: "expositionsDescriptionNumeroExpo",
              header: t("tab_expositionsDescriptionNumeroExpo"),
              enableResizing: true,
              cell: ({ row }: { row: Row<TirageInExhibition> }) => {
                return (
                  <TextCell
                    value={
                      row.original.exhibition.expositionsDescriptionNumeroExpo
                    }
                  />
                );
              },
            },
            {
              accessorKey: "tirageInExhibitionVignette",
              header: "",
              size: 100,
              cell: ({ row }: { row: Row<TirageInExhibition> }) => {
                return (
                  <ImageCell
                    value={""}
                    attachments={row.original.attachments ?? []}
                    domain={"exhibition"}
                  />
                );
              },
            },
            {
              accessorKey: "expositionsDescriptionAnnee",
              header: t("tab_expositionsDescriptionAnnee"),
              size: 10,
              cell: ({ row }: { row: Row<TirageInExhibition> }) => {
                return (
                  <TextCell
                    value={row.original.exhibition.expositionsDescriptionAnnee}
                  />
                );
              },
            },
            {
              accessorKey: "expositionsDescriptionTitre",
              header: t("tab_expositionsDescriptionTitre"),
              size: 300,
              cell: ({ row }: { row: Row<TirageInExhibition> }) => {
                return (
                  <HtmlCell
                    value={row.original.exhibition.expositionsDescriptionTitre}
                  />
                );
              },
            },
            {
              accessorKey: "expositionsDescriptionDates",
              header: t("tab_expositionsDescriptionDates"),
              cell: ({ row }: { row: Row<TirageInExhibition> }) => {
                return (
                  <TextCell
                    value={row.original.exhibition.expositionsDescriptionDates}
                  />
                );
              },
            },
            {
              accessorKey: "expositionsDescriptionLieu",
              header: t("tab_expositionsDescriptionLieu"),
              size: 300,
              cell: ({ row }: { row: Row<TirageInExhibition> }) => {
                return (
                  <div className="flex flex-col items-start text-wrap">
                    <p className="text-[14px] font-[500]">
                      {row.original.exhibition.expositionsDescriptionVille}
                    </p>
                    <p className="text-[14px] font-[400] text-gray-500">
                      {row.original.exhibition.expositionsDescriptionLieu}
                    </p>
                  </div>
                );
              },
            },
          ]
        : type === "exhibition"
          ? [
              {
                accessorKey: "tirage.tirageDescriptionNumeroEnregistrement",
                header: t("tirageDescriptionNumeroEnregistrement"),
                enableResizing: true,
                cell: ({ row }: { row: Row<TirageInExhibition> }) => {
                  return (
                    <TextCell
                      value={
                        row.original.tirage
                          .tirageDescriptionNumeroEnregistrement ?? ""
                      }
                    />
                  );
                },
              },
              {
                accessorKey: "tirageInExhibitionVignette",
                header: "",
                size: 100,
                cell: ({ row }: { row: Row<TirageInExhibition> }) => {
                  return (
                    <ImageCell
                      value={""}
                      attachments={row.original.attachments ?? []}
                      domain={"tirage"}
                    />
                  );
                },
              },
            ]
          : []),
      {
        accessorKey: "tirageDansExpositionsDescriptionNumeroCatalogue",
        header: t("tirageDansExpositionsDescriptionNumeroCatalogue"),
      },
      {
        accessorKey: "tirageDansExpositionsNotes",
        header: t("tirageDansExpositionsNotes"),
        cell: ({ row }: { row: Row<TirageInExhibition> }) => {
          return (
            <HtmlCell value={row.original.tirageDansExpositionsNotes ?? ""} />
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          const data = row.original as TirageInExhibition;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(data.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <TirageInExhibitionDrawer
                fields={fields}
                data={data}
                queryId={id}
                type={type}
              />
              {type === "exhibition" && (
                <ShowElements
                  type="tirage"
                  id={row.original.tirage.id}
                  elements={[
                    {
                      label: t("tirageDescriptionNumeroEnregistrement"),
                      value:
                        row.original.tirage
                          .tirageDescriptionNumeroEnregistrement ?? "",
                      type: "text",
                    },
                    {
                      label: t("tirageDescriptionNotes"),
                      value: row.original.tirage.tirageDescriptionNotes ?? "",
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.original.tirage.attachments ?? [],
                      type: "image",
                    },
                  ]}
                />
              )}
              {type === "tirage" && (
                <ShowElements
                  type="exhibition"
                  id={row.original.exhibition.id}
                  elements={[
                    {
                      label: t("expositionsDescriptionNumeroExpo"),
                      value:
                        row.original.exhibition
                          .expositionsDescriptionNumeroExpo,
                      type: "text",
                    },
                    {
                      label: t("expositionsDescriptionResume"),
                      value:
                        row.original.exhibition.expositionsDescriptionResume,
                      type: "richtext",
                    },
                  ]}
                />
              )}
            </div>
          );
        },
      },
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  const datatableConf = useMemo(() => {
    if (type === "exhibition") {
      return (
        <ExhibitionToTirage
          id={id}
          relatedData={complexeRelation}
          fields={fields}
          exhibition={exhibition}
          isAddMode={isAddMode}
          columnsLink={columns}
          searchContent={searchContent}
        />
      );
    } else {
      return (
        <TirageToExhibition
          id={id}
          relatedData={complexeRelation}
          fields={fields}
          tirage={tirage}
          isAddMode={isAddMode}
          columnsLink={columns}
          searchContent={searchContent}
        />
      );
    }
  }, [
    type,
    id,
    complexeRelation,
    fields,
    exhibition,
    isAddMode,
    columns,
    searchContent,
    tirage,
  ]);

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("relatedRecords")}
          </p>
        </div>
      )}
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">
          {type === "tirage" ? t("exhibitionsRelated") : t("tiragesRelated")}
        </h2>
        {!isAddMode && (
          <Button variant="outline" onClick={() => setIsAddMode(true)}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        )}
      </div>
      {datatableConf}
    </div>
  );
};

export default TirageInExhibitions;

type ExhibitionToTirageProps = LinksTableProps<TirageInExhibition> & {
  exhibition: Exhibition | undefined;
};

// Tirages liés à une exposition
const ExhibitionToTirage = ({
  id,
  relatedData,
  fields,
  exhibition,
  isAddMode,
  columnsLink,
  searchContent,
}: ExhibitionToTirageProps) => {
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notAlreadyRelated } = useGetNotRelatedResource<Tirage>({
    resultType: "tirage",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const { t } = useTranslation();

  const columnsTirages: ColumnDef<Tirage, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "tirageDescriptionNumeroEnregistrement",
        header: t("tirageDescriptionNumeroEnregistrement"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <TextCell
              value={row.original.tirageDescriptionNumeroEnregistrement ?? ""}
            />
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Tirage> }) => {
          if (!exhibition) {
            return null;
          }
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <TirageInExhibitionDrawer
                key={row.original.id}
                fields={fields}
                queryId={exhibition.id}
                type={"exhibition"}
                data={{
                  id: "",
                  tirage: row.original,
                  exhibition: exhibition,
                  tirageDansExpositionsDescriptionNumeroCatalogue: "",
                  tirageDansExpositionsNotes: "",
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [exhibition, fields, t]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsTirages}
          data={notAlreadyRelated?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notAlreadyRelated?.totalSize ?? 0,
          }}
          options={{
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      isAddMode,
      columnsTirages,
      columnsLink,
      getPagination,
      notAlreadyRelated?.data,
      notAlreadyRelated?.totalSize,
      relatedData,
    ],
  );

  return datatableConf;
};

type TirageToExhibitionProps = LinksTableProps<TirageInExhibition> & {
  tirage: Tirage | undefined;
};

// Expositions liées à un tirage
const TirageToExhibition = ({
  id,
  relatedData,
  fields,
  tirage,
  isAddMode,
  columnsLink,
  searchContent,
}: TirageToExhibitionProps) => {
  const { t } = useTranslation();
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notAlreadyRelated } = useGetNotRelatedResource<Exhibition>({
    resultType: "exhibition",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const columnsExhibitions: ColumnDef<Exhibition, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "expositionsDescriptionNumeroExpo",
        header: t("tab_expositionsDescriptionNumeroExpo"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <TextCell value={row.original.expositionsDescriptionNumeroExpo} />
          );
        },
      },
      {
        accessorKey: "expositionsDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            row.original.attachments && (
              <ImageCell
                value={"expositionsDescriptionVignette"}
                attachments={row.original.attachments}
                domain={"exhibition"}
              />
            )
          );
        },
      },
      {
        accessorKey: "expositionsDescriptionAnnee",
        header: t("tab_expositionsDescriptionAnnee"),
        size: 10,
        cell: ({ row }) => {
          return <TextCell value={row.original.expositionsDescriptionAnnee} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionTitre",
        header: t("tab_expositionsDescriptionTitre"),
        size: 300,
        cell: ({ row }) => {
          return <HtmlCell value={row.original.expositionsDescriptionTitre} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionDates",
        header: t("tab_expositionsDescriptionDates"),
        cell: ({ row }) => {
          return <TextCell value={row.original.expositionsDescriptionDates} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionLieu",
        header: t("tab_expositionsDescriptionLieu"),
        size: 300,
        cell: ({ row }) => {
          return (
            <div className="flex flex-col items-start text-wrap">
              <p className="text-[14px] font-[500]">
                {row.original.expositionsDescriptionVille}
              </p>
              <p className="text-[14px] font-[400] text-gray-500">
                {row.original.expositionsDescriptionLieu}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          if (!tirage) {
            return null;
          }
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="exhibition"
                id={row.original.id}
                elements={[
                  {
                    label: t("expositionsDescriptionNumeroExpo"),
                    value: row.original.expositionsDescriptionNumeroExpo,
                    type: "text",
                  },
                  {
                    label: t("expositionsDescriptionResume"),
                    value: row.original.expositionsDescriptionResume,
                    type: "richtext",
                  },
                ]}
              />
              <TirageInExhibitionDrawer
                key={row.original.id}
                fields={fields}
                queryId={tirage.id}
                type={"tirage"}
                data={{
                  id: "",
                  tirage: tirage,
                  exhibition: row.original,
                  tirageDansExpositionsDescriptionNumeroCatalogue: "",
                  tirageDansExpositionsNotes: "",
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [tirage, fields, t]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsExhibitions}
          data={notAlreadyRelated?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notAlreadyRelated?.totalSize ?? 0,
          }}
          options={{
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      columnsExhibitions,
      columnsLink,
      getPagination,
      isAddMode,
      notAlreadyRelated?.data,
      notAlreadyRelated?.totalSize,
      relatedData,
    ],
  );

  return datatableConf;
};
